/**
 * Перенос `presentation/providers/person_mood_provider.dart`.
 *
 * `AutoDisposeAsyncNotifierProvider<PersonMoodNotifier, PersonMoodModel>`
 * стал стором zustand: состояние — тот же `AsyncValue<PersonMoodModel>`,
 * методы — те же `toggleMood` и `changeMood`.
 *
 * Авто-dispose Riverpod повторён вручную: [usePersonMood] считает
 * подписчиков, первый запускает `build()`, последний уходит — состояние
 * возвращается к `loading`, и следующий вход на экран языка запрашивает
 * настроение заново. Без этого посетитель увидел бы настроение прошлого
 * аватара.
 *
 * Вызов со стороны страницы языка — два Dart-`watch`-а, один в один:
 *   const personMoodState = usePersonMood();
 *   const isSelected =
 *     valueOrNull(personMoodState)?.moodState === PersonMoodState.bored;
 *   onPressed={() => { void personMoodStore.getState().toggleMood(); }}
 */

import { useEffect } from 'react';
import { create } from 'zustand';

import { appRepository } from '@/data/appRepositoryImpl';
import {
  type PersonMoodModel,
  PersonMoodState,
  personMoodStateScenarioActivated,
} from '@/domain/models/personMoodModel';
import { getL10n } from '@/l10n/useL10n';
import { appStateStore, useAppStateStore } from '@/presentation/stores/appStateStore';
import {
  ASYNC_LOADING,
  type AsyncValue,
  asyncData,
  asyncError,
  valueOrNull,
} from '@/presentation/stores/asyncValue';
import { SnackbarManager } from '@/presentation/widgets/snackbar/snackbarManager';
import { AppException, stackTraceOf } from '@/utils/exceptions/appException';
import { AppFailure, FailureOp } from '@/utils/exceptions/appFailure';

/** Текст `UnimplementedError('Base Url not found')` из Dart. */
const BASE_URL_NOT_FOUND = 'Base Url not found';

/** Пути `/v1/animate/mood` у ноды нет — настроения она не знает. */
const HTTP_NOT_FOUND = 404;

export interface PersonMoodStore {
  /** `state` нотификатора. */
  readonly value: AsyncValue<PersonMoodModel>;
  /**
   * Ноды, ответившие на запрос настроения 404, — у них этой функции нет
   * (так устроена AVTR-1 Studio). Живёт, пока открыта страница: в
   * хранилище не пишется, чтобы нода, получившая настроение после
   * обновления, не осталась без кнопки навсегда.
   */
  readonly unsupportedNodes: ReadonlySet<string>;
  /** `build()` — запрашивает текущее настроение аватара. */
  readonly load: () => Promise<void>;
  readonly toggleMood: () => Promise<void>;
  readonly changeMood: (moodState: PersonMoodState) => Promise<void>;
}

/**
 * Поле `_changing` нотификатора: защита от повторного входа, пока
 * предыдущее переключение не закончилось. В отрисовке не участвует,
 * поэтому живёт рядом со стором, а не в его состоянии.
 */
let changing = false;

/** Сколько компонентов сейчас слушают стор — счётчик авто-dispose. */
let mountCount = 0;

/**
 * Номер текущего построения. Ответ, пришедший после уничтожения стора или
 * после нового `load()`, состояние не трогает — в Riverpod запись в
 * уничтоженный провайдер была бы ошибкой.
 */
let generation = 0;

/** Нода ответила «нет такого пути»: это её устройство, а не сбой. */
function isMissingMoodPath(error: unknown): boolean {
  return error instanceof AppException && error.statusCode === HTTP_NOT_FOUND;
}

/** `ref.read(appStateProvider).person?.personBaseUrl` */
function currentPersonBaseUrl(): string | null {
  return appStateStore.getState().appState.person?.personBaseUrl ?? null;
}

/** Адрес ноды аватара или исключение — общая часть `build()` и `changeMood()`. */
function requirePersonBaseUrl(): string {
  const baseUrl = currentPersonBaseUrl();
  if (baseUrl === null) throw new Error(BASE_URL_NOT_FOUND);
  return baseUrl;
}

export const usePersonMoodStore = create<PersonMoodStore>()((set, get) => ({
  value: ASYNC_LOADING,
  unsupportedNodes: new Set<string>(),

  load: async (): Promise<void> => {
    generation += 1;
    const token = generation;
    const baseUrl = currentPersonBaseUrl();
    // Нода уже сказала, что настроения у неё нет, — не спрашиваем снова:
    // иначе каждый вход на экран языка оставлял бы в сети новый 404.
    if (baseUrl !== null && get().unsupportedNodes.has(baseUrl)) return;
    set({ value: ASYNC_LOADING });
    try {
      const mood = await appRepository.fetchPersonMoodState(requirePersonBaseUrl());
      if (token !== generation) return;
      set({ value: asyncData(mood) });
    } catch (error) {
      // `build()`, бросивший исключение, в Riverpod даёт `AsyncError`.
      if (token !== generation) return;
      if (baseUrl !== null && isMissingMoodPath(error)) {
        set((store) => ({
          value: asyncError(error),
          unsupportedNodes: new Set([...store.unsupportedNodes, baseUrl]),
        }));
        return;
      }
      set({ value: asyncError(error) });
    }
  },

  toggleMood: async (): Promise<void> => {
    const mood = valueOrNull(get().value)?.moodState;
    if (mood === undefined) return;
    // Dart не ждал `changeMood`: кнопка не блокируется, повторное нажатие
    // отсекает `_changing`.
    void get().changeMood(
      mood === PersonMoodState.bored ? PersonMoodState.normal : PersonMoodState.bored,
    );
  },

  changeMood: async (moodState: PersonMoodState): Promise<void> => {
    if (changing) return;
    try {
      const baseUrl = requirePersonBaseUrl();
      changing = true;
      const updatedState = await appRepository.changePersonMood(baseUrl, moodState);
      set({ value: asyncData(updatedState) });
      const l10n = getL10n();
      SnackbarManager.instance.showSuccess({
        title: personMoodStateScenarioActivated(updatedState.moodState)
          ? l10n.scenarios_enabled
          : l10n.scenarios_disabled,
      });
    } catch (error) {
      SnackbarManager.instance.showFailure(
        AppFailure.fromException(FailureOp.personMood, error, stackTraceOf(error)),
      );
    } finally {
      changing = false;
    }
  },
}));

/**
 * Хранилище вне React — то же соглашение, что у [useAppStateStore]:
 * отдельное имя, чтобы вызов вне компонента не выглядел вызовом хука.
 */
export const personMoodStore = usePersonMoodStore;

/** Первый подписчик строит провайдер. */
function retain(): void {
  mountCount += 1;
  if (mountCount === 1) void personMoodStore.getState().load();
}

/** Последний подписчик уничтожает его. */
function release(): void {
  mountCount -= 1;
  if (mountCount > 0) return;
  generation += 1;
  changing = false;
  personMoodStore.setState({ value: ASYNC_LOADING });
}

/** `ref.watch(personMoodProvider)` вместе с построением провайдера. */
export function usePersonMood(): AsyncValue<PersonMoodModel> {
  useEffect(() => {
    retain();
    return release;
  }, []);
  return usePersonMoodStore((store) => store.value);
}

/**
 * Есть ли настроение у ноды выбранного аватара. Пока нода не ответила —
 * есть: на рабочих нодах кнопка сценариев не должна мигать при входе.
 */
export function usePersonMoodSupported(): boolean {
  const baseUrl = useAppStateStore((store) => store.appState.person?.personBaseUrl ?? null);
  return usePersonMoodStore(
    (store) => baseUrl === null || !store.unsupportedNodes.has(baseUrl),
  );
}
